<template>
  <div>
    <v-container>
      <v-row>
        <h2 class="pb-2 pt-2">Pünktlichkeit</h2>
      </v-row>
      <v-row>
        <v-col cols="12" class="pt-0">
          <small class="text-medium-emphasis">Datenbasis: Fernverkehr</small>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" class="py-0">
          <AnonymousNotice />
        </v-col>
      </v-row>

      <template v-if="summary">
        <v-row>
          <v-col cols="12" class="py-0">
            <v-switch
              v-model="showCancelled"
              label="Ausfälle anzeigen"
              color="primary"
              density="compact"
              hide-details
              inset
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" sm="6" md="3">
            <v-card>
              <v-card-text>
                <div class="text-h4">{{ percent(summary.onTimeRate) }}</div>
                <div class="text-medium-emphasis">pünktlich (&lt;6 min)</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card>
              <v-card-text>
                <div class="text-h4">{{ percent(cancelRate) }}</div>
                <div class="text-medium-emphasis">Ausfälle</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <h3 class="pt-4 pb-2">Verlauf</h3>
            <div style="height: 360px">
              <BarChart :datasets="trend.datasets" :labels="trend.labels" :options="stackedOptions"/>
            </div>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" md="6">
            <h3 class="pt-4 pb-2">Schlechteste Bahnhöfe</h3>
            <div style="height: 500px">
              <BarChart :datasets="stations.datasets" :labels="stations.labels" :options="horizontalPercentOptions"/>
            </div>
          </v-col>
          <v-col cols="12" md="6">
            <h3 class="pt-4 pb-2">Nach Kategorie</h3>
            <div style="height: 500px">
              <BarChart :datasets="categories.datasets" :labels="categories.labels" :options="stackedOptions"/>
            </div>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <h3 class="pt-4 pb-2">Tagesgang</h3>
            <div style="height: 360px">
              <BarChart :datasets="hours.datasets" :labels="hours.labels" :options="stackedOptions"/>
            </div>
          </v-col>
        </v-row>
      </template>
      <LoadingIndicator v-else/>
    </v-container>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import AnonymousNotice from '@/components/AnonymousNotice.vue'
import BarChart from '@/components/BarChart.vue'
import LoadingIndicator from '@/components/LoadingIndicator.vue'
import SearchRangeMixin from '@/mixins/SearchRangeMixin'
import TitleMixin from '@/mixins/TitleMixin'
import { AMPEL_CLASSES } from '@/utils/ampelColors'

// Tooltip line for a count-stacked Ampel bar: absolute count plus its share of the bar's total.
function ampelTooltipLabel(context) {
  const value = Number(context.raw) || 0;
  const total = context.chart.data.datasets.reduce(
    (sum, dataset) => sum + (Number(dataset.data[context.dataIndex]) || 0), 0);
  const share = total ? ((value / total) * 100).toFixed(1) : '0.0';
  return `${context.dataset.label}: ${value} (${share} %)`;
}

// Tooltip line for a 100%-normalised Ampel bar: the share (bar value) plus the absolute count,
// which is carried on the dataset's `counts` array (see toStackedPercent).
function ampelPercentTooltipLabel(context) {
  const share = Number(context.raw) || 0;
  const counts = context.dataset.counts || [];
  const count = Number(counts[context.dataIndex]) || 0;
  return `${context.dataset.label}: ${share.toFixed(1)} % (${count})`;
}

export default {
  name: 'PunctualityDashboard',

  components: {
    AnonymousNotice,
    BarChart,
    LoadingIndicator,
  },

  mixins: [SearchRangeMixin, TitleMixin],

  data: () => ({
    err: null,
    summary: null,
    // Toggle: when false, the Ausfall (cancelled) class is dropped from every chart, and the
    // 100%-stack denominator becomes the trains that actually ran.
    showCancelled: true,
    trendBuckets: [],
    stationBuckets: [],
    categoryBuckets: [],
    hourBuckets: [],
    // 100%-normalised horizontal stack: the on-time (green) share visibly shrinks toward the
    // worst stations at the top, so rank and picture match. Absolute counts stay in the tooltip.
    horizontalPercentOptions: {
      maintainAspectRatio: false,
      indexAxis: 'y',
      scales: {
        x: { stacked: true, min: 0, max: 100, ticks: { callback: value => `${value} %` } },
        y: { stacked: true },
      },
      plugins: {
        legend: { position: 'bottom' },
        tooltip: { callbacks: { label: ampelPercentTooltipLabel } },
      },
    },
    stackedOptions: {
      maintainAspectRatio: false,
      scales: { x: { stacked: true }, y: { stacked: true } },
      plugins: {
        legend: { position: 'bottom' },
        tooltip: { callbacks: { label: ampelTooltipLabel } },
      },
    },
  }),

  computed: {
    ...mapState({ searchRange: state => state.search.range }),

    title() {
      return `Pünktlichkeit - ${import.meta.env.VITE_NAME}`;
    },

    cancelRate() {
      if (!this.summary || this.summary.total === 0) {
        return 0;
      }
      return this.summary.cancelled / this.summary.total;
    },

    // The Ampel classes currently drawn — all of them, or without Ausfall when the toggle is off.
    activeClasses() {
      return this.showCancelled ? AMPEL_CLASSES : AMPEL_CLASSES.filter(cls => cls.key !== 'cancelled');
    },

    // Chart data derived reactively from the raw buckets + the active classes, so flipping the
    // toggle re-renders without re-fetching.
    trend() {
      return this.toStacked(this.trendBuckets);
    },
    stations() {
      return this.toStackedPercent(this.stationBuckets);
    },
    categories() {
      return this.toStacked(this.categoryBuckets);
    },
    hours() {
      return this.toStacked(this.hourBuckets);
    },
  },

  methods: {
    percent(rate) {
      return `${(rate * 100).toFixed(1)} %`;
    },

    queryFor(groupBy) {
      const range = this.searchRangeToQuery(this.searchRange);
      const separator = range ? '&' : '?';
      return `/punctuality${range}${separator}groupBy=${groupBy}`;
    },

    toStacked(buckets) {
      return {
        labels: buckets.map(bucket => bucket.label),
        datasets: this.activeClasses.map(cls => ({
          label: cls.label,
          backgroundColor: cls.color,
          data: buckets.map(bucket => bucket[cls.key]),
        })),
      };
    },

    // Like toStacked, but each segment is its share (%) of the bucket total (over the active
    // classes only), so bars fill 100%. The absolute counts ride along on `counts` for the tooltip.
    toStackedPercent(buckets) {
      const totals = buckets.map(bucket => this.activeClasses.reduce((sum, cls) => sum + bucket[cls.key], 0));
      return {
        labels: buckets.map(bucket => bucket.label),
        datasets: this.activeClasses.map(cls => ({
          label: cls.label,
          backgroundColor: cls.color,
          data: buckets.map((bucket, i) => (totals[i] ? (bucket[cls.key] / totals[i]) * 100 : 0)),
          counts: buckets.map(bucket => bucket[cls.key]),
        })),
      };
    },

    load() {
      this.summary = null;
      this.err = null;

      const trend = this.$api.get(this.queryFor('day'))
        .then(response => {
          this.summary = response.data.summary;
          this.trendBuckets = response.data.buckets;
        });
      const stations = this.$api.get(this.queryFor('station'))
        .then(response => { this.stationBuckets = response.data.buckets; });
      const categories = this.$api.get(this.queryFor('category'))
        .then(response => { this.categoryBuckets = response.data.buckets; });
      const hours = this.$api.get(this.queryFor('hour'))
        .then(response => { this.hourBuckets = response.data.buckets; });

      Promise.all([trend, stations, categories, hours]).catch(err => {
        this.err = err;
        this.$log.error(err);
      });
    },
  },

  mounted() {
    this.load();
  },

  watch: {
    searchRange() {
      this.load();
    },
  },
};
</script>
